import React, { Component, Fragment } from 'react'
import { Layout } from 'app/common/layout'
import { Push } from 'app/common/push'
import styled from 'styled-components'
import { Cake, Face } from 'app/assets/icons'
import birthday from 'app/assets/images/birthday/birthday.png'
import KingBirthday from 'app/assets/images/kings-birthday/kingBirthday.jpg'
import { H1, H2, Text } from 'app/common/typography'
import { FormattedMessage, injectIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { FooterNotice } from 'app/common/footer-notice'
import { FeaturedProducts } from 'app/common/featured-products'
import { Picture } from 'react-responsive-picture'
import { featureCodes, toSrcSet } from 'shared/utils'
import { media, ResponsiveContainer } from 'app/assets/styles'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import { Breadcrumb } from 'app/common/breadcrumb'
import { Picture as PictureComponent } from 'app/common/picture'
import type { EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import { Cards } from 'app/common/cards'
import { Link } from 'app/common/link'
import { ErrorManagement } from 'app/common/error-management'
import type {
    ImageShortDTO,
    MarketingAnimationShortDTO,
    SuggestionShortDTO
} from 'shared/types'
import { Placeholder } from 'app/common/placeholder'
import { FeatureToggle } from '../common/feature-toggle'

type Props = {
    fetchFamilyPageData: () => void,
    endowments: ?Array<MarketingAnimationShortDTO>,
    imageHeader: ?ImageShortDTO,
    offer: ?MarketingAnimationShortDTO,
    suggestions: ?SuggestionShortDTO,
    footerNotice: ?MarketingAnimationShortDTO,
    sendMeta: EventMetadata => void,
    error: ?any,
    networkError: any,
    intl: IntlShape,
    dataLoaded: boolean
}

class Family extends Component<Props> {
    componentDidMount() {
        this.props.fetchFamilyPageData()
    }

    handleAnniversaryOfferClick = () => {
        this.props.sendMeta(eventsDefinitions['FAMILY_ANNIVERSARY'])
    }

    render() {
        const {
            endowments,
            imageHeader,
            offer,
            suggestions,
            footerNotice,
            intl,
            error,
            networkError
        } = this.props

        return (
            <ErrorManagement error={error} networkError={networkError}>
                <Layout>
                    <MainContainer>
                        <BreadcrumbContainer>
                            <Breadcrumb
                                breadcrumbArray={[
                                    {
                                        title: intl.formatMessage({
                                            id: 'family.title'
                                        })
                                    }
                                ]}
                            />
                        </BreadcrumbContainer>
                        <H1>
                            <FormattedMessage id="family.main.title" />
                        </H1>
                        <Text>
                            <FormattedMessage id="family.main.description" />
                        </Text>
                    </MainContainer>
                    <Placeholder
                        minHeightDesktop="37vw"
                        minHeightMobile="140vw">
                        {imageHeader && (
                            <PictureComponent
                                imageUrlLandscape={
                                    imageHeader.imageUrlLandscape
                                }
                                imageUrlPortrait={imageHeader.imageUrlPortrait}
                                desktopRatio="16/7"
                                mobileRatio="2/3"
                            />
                        )}
                    </Placeholder>
                    {suggestions && suggestions.products && (
                        <CenteredContainer>
                            <Section>
                                <FormattedMessage id="family.menu.title" />
                            </Section>
                            <Text>
                                <FormattedMessage id="family.menu.description" />
                            </Text>
                            <FeaturedProducts
                                title=""
                                padding="0"
                                align="left"
                                suggestions={suggestions}
                            />
                        </CenteredContainer>
                    )}
                    {endowments && (
                        <Fragment>
                            <SurprisesContainer>
                                <Cards
                                    secondary={endowments}
                                    title="family.surprises.title"
                                    subTitle="family.surprises.description"
                                    padding="0"
                                    background="transparent"
                                />
                            </SurprisesContainer>
                            <SurprisesMobileContainer>
                                <ResponsiveContainer>
                                    <Section>
                                        <FormattedMessage id="family.surprises.title" />
                                    </Section>
                                    <Text>
                                        <FormattedMessage id="family.surprises.description" />
                                    </Text>
                                    {endowments.map(
                                        (
                                            endowment: MarketingAnimationShortDTO,
                                            index
                                        ) => (
                                            <Link
                                                key={index}
                                                url={endowment.actionUrl}
                                                external={endowment.external}>
                                                <Picture
                                                    style={{
                                                        width: '100%',
                                                        marginTop: '20px',
                                                        marginBottom: '20px'
                                                    }}
                                                    sources={[
                                                        {
                                                            srcSet: toSrcSet(
                                                                endowment.imageUrlCropped
                                                            )
                                                        }
                                                    ]}
                                                />
                                                <TextContainer>
                                                    <Text>
                                                        {endowment.subtitle}
                                                    </Text>
                                                </TextContainer>
                                            </Link>
                                        )
                                    )}
                                </ResponsiveContainer>
                            </SurprisesMobileContainer>
                        </Fragment>
                    )}
                    <CenteredContainer>
                        <Section>
                            <FormattedMessage id="family.parents.title" />
                        </Section>
                        <Description>
                            <Text>
                                <FormattedMessage id="family.parents.description" />
                            </Text>
                        </Description>
                    </CenteredContainer>
                    <OffersContainer>
                        {offer && (
                            <Push
                                backgroundImage={
                                    offer.imageUrlLandscape.slice(-1)[0]
                                }
                                backgroundImagePortrait={
                                    offer.imageUrlPortrait.slice(-1)[0]
                                }
                                textColor="white"
                                btnTextColor="white"
                                title={offer.title}
                                baseline={offer.subtitle}
                                button={offer.actionLabel}
                                actionUrl={offer.actionUrl}
                                ratio={1}
                            />
                        )}
                        <Push
                            background={oldColors.oceanBlue}
                            btnBackground="white"
                            textColor="white"
                            btnTextColor="black"
                            image={[birthday]}
                            title={intl.formatMessage({
                                id: 'family.birthday.title'
                            })}
                            button={intl.formatMessage({
                                id: 'family.birthday.button'
                            })}
                            actionUrl={intl.formatMessage({
                                id: 'routes.birthday'
                            })}
                            handleClick={this.handleAnniversaryOfferClick}>
                            <Cake />
                        </Push>
                        <GamesOffersContainer>
                            <Push
                                background={colors.crunchyGreen}
                                btnBackground="white"
                                textColor="white"
                                btnTextColor="black"
                                title={intl.formatMessage({
                                    id: 'family.game.title'
                                })}
                                baseline={intl.formatMessage({
                                    id: 'family.game.description'
                                })}
                                button={intl.formatMessage({
                                    id: 'home.restaurants.proximity.button'
                                })}
                                actionUrl="restaurants?filters=PLAYGROUND"
                                ratio={1}
                                customStyle="height:50%;">
                                <Face />
                            </Push>
                            <FeatureToggle feature={featureCodes.KING_BIRTHDAY}>
                                <KingBirthdayPicture>
                                    <Push
                                        backgroundImage={KingBirthday}
                                        customStyle={'height: 100%;'}
                                        ratio={1}
                                    />
                                </KingBirthdayPicture>
                            </FeatureToggle>
                        </GamesOffersContainer>
                    </OffersContainer>
                    {footerNotice && footerNotice.actionLabel && (
                        <FooterNotice {...footerNotice} />
                    )}
                </Layout>
            </ErrorManagement>
        )
    }
}

const MainContainer = styled(ResponsiveContainer)`
    margin-top: 80px;
    margin-bottom: 20px;
    text-align: center;
    ${media.medium`
        margin-top: 90px;
        margin-bottom: 25px;
    `};
`
const CenteredContainer = styled(ResponsiveContainer)`
    text-align: center;
`
const BreadcrumbContainer = styled.div`
    display: none;
    ${media.medium`
        postion: absolute;
        text-align: left;
        display: block;
    `};
`

const SurprisesContainer = styled.div`
    display: none;
    ${media.medium`
        display: block;
        background: ${oldColors.tertiary};
    `};
`

const SurprisesMobileContainer = styled.div`
    display: block;
    padding-bottom: 20px;
    text-align: center;
    background: ${oldColors.tertiary};
    ${media.medium`
        display: none;
    `};
    img {
        border-radius: 12px;
        width: 100%;
        box-shadow: 0 20px 30px -20px rgba(102, 102, 102, 30);
    }
`

const Section = styled(H2)`
    padding-bottom: 20px;
    padding-top: 20px;
`

const Description = styled.div`
    margin-bottom: 20px;
    line-height: 20px;
`
const TextContainer = styled.div``

const OffersContainer = styled.section`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    height: auto;
    margin: 0 auto;
`
const GamesOffersContainer = styled.section`
    width: 100%;
    ${media.medium`
        width: 50%;
    `};
`
const KingBirthdayPicture = styled.div`
    display: none;
    ${media.medium`
        display: block;
        height: 50%;
    `};
`

export const FamilyComponent = injectIntl(Family)
